import { useEffect, useState, useMemo } from "react";
import SubscriptionManager from "../../services/dashboard/SubscriptionManager";
import { useNavigate } from "react-router-dom";
import { UserRound, History, X } from "lucide-react";

const SubscribedUsers = () => {
  const [allSubscribedUsers, setAllSubscribedUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [showExtensionHistory, setShowExtensionHistory] = useState(false);
  const [selectedUserExtensions, setSelectedUserExtensions] = useState([]);
  const [extensionLoading, setExtensionLoading] = useState(false);
  const navigate = useNavigate();

  // Define enum ordering for packageType and planName
  const packageTypeOrder = {
    ONE_BOOK: 1,
    TWO_BOOKS: 2,
    THREE_BOOKS: 3,
  };

  const planNameOrder = {
    ONE_MONTH: 1,
    THREE_MONTHS: 3,
    SIX_MONTHS: 6,
    ONE_YEAR: 12,
  };

  // Fetch the full list once, on mount
  useEffect(() => {
    const fetchSubscribedUsers = async () => {
      setLoading(true);
      try {
        const data = await SubscriptionManager.getAllSubscribedUsersWithDetails();
        setAllSubscribedUsers(data || []);
        setDisplayedUsers(data || []);
      } catch (error) {
        console.error("Failed to fetch subscribed users:", error);
        setAllSubscribedUsers([]);
        setDisplayedUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscribedUsers();
  }, []);

  // Filter displayedUsers when searchTerm changes
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setDisplayedUsers(allSubscribedUsers);
      return;
    }

    const lower = searchTerm.trim().toLowerCase();
    const filtered = allSubscribedUsers.filter((u) => {
      const nameMatch = u.userName?.toLowerCase().includes(lower);
      const emailMatch = u.emailId?.toLowerCase().includes(lower);
      const planMatch = u.planName?.toLowerCase().includes(lower);
      return nameMatch || emailMatch || planMatch;
    });

    setDisplayedUsers(filtered);
  }, [searchTerm, allSubscribedUsers]);

  // Helper: convert endDate array → JS Date
  const parseDateArray = (dateArray) => {
    if (
      !Array.isArray(dateArray) ||
      dateArray.length < 6 ||
      dateArray.some((n) => typeof n !== "number")
    ) {
      return null;
    }
    const [year, month, day, hour, minute, second] = dateArray;
    return new Date(year, month - 1, day, hour, minute, second);
  };

  // Format date for display
  const formatDateArray = (dateArray) => {
    const d = parseDateArray(dateArray);
    if (!d) return "N/A";
    return d.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Sorting handler (currently unused but kept for future functionality)
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // toggle direction
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  // Memoized sorted list based on displayedUsers + sortConfig
  const sortedUsers = useMemo(() => {
    const sortable = [...displayedUsers];
    const { key, direction } = sortConfig;
    if (!key) return sortable;

    sortable.sort((a, b) => {
      // Custom sort for planName enum
      if (key === "planName") {
        const aVal = planNameOrder[a.planName] || 0;
        const bVal = planNameOrder[b.planName] || 0;
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      // Custom sort for packageType enum
      if (key === "packageType") {
        const aVal = packageTypeOrder[a.packageType] || 0;
        const bVal = packageTypeOrder[b.packageType] || 0;
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      let aVal = a[key];
      let bVal = b[key];

      // If sorting by endDate or startDate, compare by actual Date objects
      if (key === "endDate" || key === "startDate") {
        const aDate = parseDateArray(aVal);
        const bDate = parseDateArray(bVal);
        if (!aDate && !bDate) return 0;
        if (!aDate) return direction === "asc" ? -1 : 1;
        if (!bDate) return direction === "asc" ? 1 : -1;
        return direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      // Strings vs numbers
      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sortable;
  }, [displayedUsers, sortConfig]);

  const handleViewUserDetails = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const handleViewExtensionHistory = async (userId, userName) => {
    setExtensionLoading(true);
    try {
      const subscriptions = await SubscriptionManager.getUserSubscriptions(userId);
      
      // Filter only extended subscriptions (where updated_date exists and is different from creation_date)
      const extendedSubscriptions = subscriptions.filter(sub => {
        // Check if subscription was modified after creation
        const creationDate = new Date(sub.creationDate);
        const updatedDate = sub.updatedDate ? new Date(sub.updatedDate) : null;
        
        return updatedDate && updatedDate.getTime() !== creationDate.getTime();
      });

      setSelectedUserExtensions({
        userName,
        userId,
        extensions: extendedSubscriptions
      });
      setShowExtensionHistory(true);
    } catch (error) {
      console.error("Failed to fetch extension history:", error);
    } finally {
      setExtensionLoading(false);
    }
  };

  const closeExtensionHistory = () => {
    setShowExtensionHistory(false);
    setSelectedUserExtensions([]);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-[1800px] mx-auto p-6">
        {/* Back Button */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          ← Back
        </button>
        
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-8">
          {/* Title + Description */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
              Subscribed Users
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Active subscription management
            </p>
          </div>

          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search by name, email, or plan..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 focus:shadow-lg transition-all duration-200 shadow-sm hover:border-gray-400 dark:hover:border-gray-600"
            />
          </div>
        </div>

        {/* Elite Table Container */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400 text-lg animate-pulse">Loading...</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.3)] border border-gray-200/60 dark:border-gray-700/50 overflow-hidden ring-1 ring-black/5 dark:ring-white/5">
            <div className="overflow-x-auto elite-scrollbar">
              <style>{`
                .elite-scrollbar::-webkit-scrollbar {
                  height: 6px;
                }
                .elite-scrollbar::-webkit-scrollbar-track {
                  background: transparent;
                }
                .elite-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(203 213 225);
                  border-radius: 3px;
                  transition: background-color 0.2s ease;
                }
                .dark .elite-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(71 85 105);
                }
                .elite-scrollbar:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(148 163 184);
                }
                .dark .elite-scrollbar:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(100 116 139);
                }
              `}</style>
              <table className="min-w-full">
                {/* Elite Header with Enhanced Contrast */}
                <thead className="bg-gray-100/80 dark:bg-gray-900/70 border-b border-gray-200 dark:border-gray-700/70 backdrop-blur-sm">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">User</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Plan</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Duration</th>
                    <th className="px-3 py-2.5 text-center text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                
                {/* Elite Premium Table Body - Final Elite Polish */}
                <tbody className="bg-white dark:bg-gray-800">
                  {sortedUsers.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-12 text-gray-500 dark:text-gray-400">
                        {searchTerm ? `No users match "${searchTerm}."` : "No subscribed users found."}
                      </td>
                    </tr>
                  ) : (
                    sortedUsers.map((user) => (
                      <tr
                        key={user.userId}
                        className="group border-b border-black/[0.04] dark:border-white/[0.04] transition-all duration-200 ease-out hover:bg-black/[0.02] dark:hover:bg-white/[0.04] cursor-pointer"
                      >
                        {/* User Column - Name + Email */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-semibold text-gray-950 dark:text-white transition-colors duration-150">
                              {user.userName}
                            </span>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">
                              {user.emailId}
                            </span>
                          </div>
                        </td>

                        {/* Plan Column - Tightened */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-150">
                              {user.planDisplayName || user.planName?.replace('_', ' ')}
                            </span>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">
                              {user.packageName || user.packageType?.replace('_', ' ')} · {user.planName?.replace('_', ' ').toLowerCase()} · {user.packageType?.replace('_', ' ').toLowerCase()}
                            </span>
                          </div>
                        </td>

                        {/* Duration Column - Tightened Block */}
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5">
                              <span className={`inline-flex items-center gap-1 text-sm font-semibold transition-colors duration-150 ${
                                user.remainingDays < 7
                                  ? "text-red-700 dark:text-red-400"
                                  : "text-green-700 dark:text-green-400"
                              }`}>
                                <span className={`w-1.5 h-1.5 rounded-full transition-all duration-150 ${
                                  user.remainingDays < 7
                                    ? "bg-red-500 dark:bg-red-400 shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                                    : "bg-green-500 dark:bg-green-400 shadow-[0_0_3px_rgba(34,197,94,0.5)]"
                                }`}></span>
                                {user.remainingDays} days left
                              </span>
                              {user.isExtended && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-orange-50/40 dark:bg-orange-900/15 text-orange-700 dark:text-orange-400 border border-orange-200/30 dark:border-orange-800/25 shadow-sm transition-all duration-150">
                                  +{user.extensionDays}d
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 transition-colors duration-150">
                              Ends {formatDateArray(user.endDate)}
                            </span>
                          </div>
                        </td>

                        {/* Actions Column - Show on Hover Only */}
                        <td className="px-3 py-2.5 relative">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => handleViewUserDetails(user.userId)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                              title="View User Details"
                            >
                              <UserRound className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Extension History Modal */}
      {showExtensionHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                Extension History - {selectedUserExtensions.userName}
              </h2>
              <button
                onClick={closeExtensionHistory}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {selectedUserExtensions.extensions && selectedUserExtensions.extensions.length > 0 ? (
                <div className="space-y-4">
                  {selectedUserExtensions.extensions.map((extension, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h3 className="font-semibold text-gray-800 dark:text-white mb-2">Subscription Details</h3>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Plan:</span>
                              <span className="font-medium text-gray-800 dark:text-white">
                                {extension.bookPackagePlan?.membershipPlan?.replace('_', ' ')}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Package:</span>
                              <span className="font-medium text-gray-800 dark:text-white">
                                {extension.bookPackage?.packageType?.replace('_', ' ')}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Status:</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                extension.status === 'ACTIVE' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                                {extension.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h3 className="font-semibold text-gray-800 dark:text-white mb-2">Extension Timeline</h3>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Created:</span>
                              <span className="font-medium text-gray-800 dark:text-white">
                                {new Date(extension.creationDate).toLocaleDateString('en-IN', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-orange-600 dark:text-orange-400">Extended:</span>
                              <span className="font-medium text-orange-800 dark:text-orange-200">
                                {new Date(extension.updatedDate).toLocaleDateString('en-IN', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Days Extended:</span>
                              <span className="font-bold text-orange-600 dark:text-orange-400">
                                {Math.ceil((new Date(extension.updatedDate) - new Date(extension.creationDate)) / (1000 * 60 * 60 * 24))} days later
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Start Date:</span>
                            <div className="font-medium text-gray-800 dark:text-white">
                              {new Date(extension.startDate).toLocaleDateString('en-IN')}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">End Date:</span>
                            <div className="font-medium text-gray-800 dark:text-white">
                              {new Date(extension.endDate).toLocaleDateString('en-IN')}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Total Duration:</span>
                            <div className="font-medium text-gray-800 dark:text-white">
                              {Math.ceil((new Date(extension.endDate) - new Date(extension.startDate)) / (1000 * 60 * 60 * 24))} days
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <History className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2">No Extension History</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    This user has no subscription extensions on record.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscribedUsers;
